import i18n from '../../../i18n';

/**
 * Resolves a localized broadcast success message from the backend response status message.
 * Matches patterns such as "Broadcast initiated for 2 tariffs." and applies internationalization.
 *
 * @param {string | undefined} rawMessage - The raw backend status message.
 * @param {'tariffs' | 'locations' | 'tokens'} entityType - The entity type being broadcasted.
 * @returns {string} The localized success message.
 */
export const resolveBroadcastSuccessMessage = (
  rawMessage: string | undefined,
  entityType: 'tariffs' | 'locations' | 'tokens',
): string => {
  if (rawMessage) {
    const match = rawMessage.match(/\b(\d+)\b/);
    if (match) {
      const count = parseInt(match[1], 10);
      if (entityType === 'tariffs') {
        return count === 1
          ? i18n.t('ocpi:broadcastInitiatedForTariff', { count })
          : i18n.t('ocpi:broadcastInitiatedForTariffs', { count });
      }
      if (entityType === 'locations') {
        return count === 1
          ? i18n.t('ocpi:broadcastInitiatedForLocation', { count })
          : i18n.t('ocpi:broadcastInitiatedForLocations', { count });
      }
      if (entityType === 'tokens') {
        return count === 1
          ? i18n.t('ocpi:broadcastInitiatedForToken', { count })
          : i18n.t('ocpi:broadcastInitiatedForTokens', { count });
      }
    }
  }

  return i18n.t('ocpi:broadcastInitiated', { defaultValue: 'Broadcast initiated successfully.' });
};

/**
 * Resolves a localized broadcast error message from an error response or object.
 *
 * @param {any} error - The error thrown by the API call.
 * @returns {string} The localized error message.
 */
export const resolveBroadcastErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.response?.data?.status_message) {
    return error.response.data.status_message;
  }
  return (
    error?.message ||
    i18n.t('ocpi:failedToBroadcast', { defaultValue: 'Failed to broadcast.' })
  );
};
